import { createSelector } from 'reselect'

import API from '../../api/api'

export const GET_MY_BOOKINGS = 'RESERVATIONS/GET_MY_BOOKINGS'
export const GET_AMENITIES = 'RESERVATIONS/GET_AMENITIES'
export const ADD_NEW_AMENITY = 'RESERVATIONS/ADD_NEW_AMENITY'
export const UPSERT_AMENITY = 'RESERVATIONS/UPSERT_AMENITY'
export const DELETE_AMENITY = 'RESERVATIONS/DELETE_AMENITY'
export const GET_RESERVATIONS = 'RESERVATIONS/GET_RESERVATIONS'
export const GET_MULTIDAY_RESERVATIONS = 'GET_MULTIDAY_RESERVATIONS'
export const GET_AMENITY_RESERVATIONS = 'RESERVATIONS/GET_AMENITY_RESERVATIONS'
export const GET_MULTIDAY_AMENITY_RESERVATIONS = 'RESERVATIONS/GET_MULTIDAY_AMENITY_RESERVATIONS'
export const CANCEL_RESERVATION = 'RESERVATIONS/CANCEL_RESERVATION'
export const CANCEL_BOOKING = 'RESERVATIONS/CANCEL_BOOKING'
export const CANCEL_AMENITY_RESERVATION = 'RESERVATIONS/CANCEL_AMENITY_RESERVATIONS'
export const GET_REQUESTS = 'RESERVATIONS/GET_REQUESTS'
export const SET_STATUS = 'RESERVATIONS/SET_REQUEST_STATUS'
export const SET_BLOCKED_DAYS = 'RESERVATIONS/SET_BLOCKED_DAYS'
export const CLEAR_RESERVATIONS = 'RESERVATIONS/CLEAR_RESERVATIONS'
export const FETCH_RESERVATION_DASHBOARD = 'RESERVATIONS/FETCH_RESERVATION_DASHBOARD'
export const TOGGLE_DASHBOARD = 'RESERVATIONS/TOGGLE_DASHBOARD'

const initialState = {
  myBookings: [],
  amenities: [],
  reservations: {},
  requests: [],
  amenityReservations: [],
  amenityMultiDayReservations: [],
  blockedDays: [],
  dashboard: {
    count: null,
    countPending: null,
    countCompleted: null,
    countBlocked: null,
    countApproved: null,
    countCancelled: null,
    results: [],
  },
  isDashboardToggled: false,
}

const uniqEntities = (currentEntities, newEntities) => {
  const filtered = currentEntities.filter(
    (entity) => newEntities.find((newEntity) => newEntity.id === entity.id) === undefined,
  )

  return [...filtered, ...newEntities]
}

const reservationsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_MY_BOOKINGS: {
      return {
        ...state,
        myBookings: action.myBookings,
      }
    }
    case GET_AMENITIES: {
      return {
        ...state,
        amenities: action.amenities,
      }
    }
    case ADD_NEW_AMENITY: {
      return {
        ...state,
        amenities: [action.amenity, ...state.amenities],
      }
    }
    case UPSERT_AMENITY: {
      const exists = state.amenities.some((amenity) => amenity.id === action.amenity.id)
      return {
        ...state,
        amenities: exists
          ? state.amenities.map((amenity) =>
              amenity.id === action.amenity.id ? { ...amenity, ...action.amenity } : amenity,
            )
          : [action.amenity, ...state.amenities],
      }
    }
    case DELETE_AMENITY:
      return {
        ...state,
        amenities: state.amenities.filter((amenity) => amenity.id !== action.amenityId),
      }
    case GET_RESERVATIONS:
      return {
        ...state,
        reservations: { [action.timestamp]: action.reservations },
      }
    case GET_MULTIDAY_RESERVATIONS:
      return {
        ...state,
        reservations: {
          [action.timestamp]: uniqEntities(
            state.reservations[action.timestamp],
            action.reservations,
          ),
        },
      }
    case GET_AMENITY_RESERVATIONS:
      return {
        ...state,
        amenityReservations: action.reservations,
      }
    case GET_MULTIDAY_AMENITY_RESERVATIONS:
      return {
        ...state,
        amenityMultiDayReservations: action.reservations,
      }
    case CANCEL_AMENITY_RESERVATION:
      const foundAmenity = state.amenityReservations.find((amenity) =>
        amenity.reservations.some((reservation) => reservation.id === action.reservationId),
      )
      const updatedAmenityReservation = {
        ...foundAmenity,
        reservations: foundAmenity.reservations.map((res) =>
          res.id === action.reservationId
            ? {
                ...res,
                cancelled: action.updatedData.cancelled,
                refunded: action.updatedData.refunded,
              }
            : res,
        ),
      }
      const updatedAmenities = state.amenityReservations.map((amenity) =>
        amenity === foundAmenity ? updatedAmenityReservation : amenity,
      )
      return {
        ...state,
        amenityReservations: updatedAmenities,
      }
    case CANCEL_BOOKING:
      return {
        ...state,
        myBookings: state.myBookings.map((booking) =>
          booking.id === action.bookingId ? { ...booking, amenityId: action.updatedData } : booking,
        ),
      }
    case CANCEL_RESERVATION:
      return {
        ...state,
        reservations: {
          ...state.reservations,
          [action.timestamp]: state.reservations[action.timestamp].map((reservation) => {
            if (reservation.reservations) {
              return {
                ...reservation,
                reservations: reservation.reservations.map((res) =>
                  res.id === action.reservationId
                    ? {
                        ...res,
                        cancelled: action.updatedData.cancelled,
                        refunded: action.updatedData.refunded,
                      }
                    : res,
                ),
              }
            } else {
              return {
                ...reservation,
                cancelled:
                  reservation.id === action.reservationId
                    ? action.updatedData.cancelled
                    : reservation.cancelled,
                refunded:
                  reservation.id === action.reservationId
                    ? action.updatedData.refunded
                    : reservation.refunded,
              }
            }
          }),
        },
      }
    case GET_REQUESTS:
      return {
        ...state,
        requests: action.requests,
      }
    case SET_BLOCKED_DAYS:
      return {
        ...state,
        blockedDays: action.blockedDays,
      }
    case SET_STATUS:
      return {
        ...state,
        requests: state.requests.filter((request) => !action.ids.includes(request.id)),
      }
    case CLEAR_RESERVATIONS:
      return {
        ...state,
        amenityReservations: [],
        amenityMultiDayReservations: [],
        reservations: [],
      }
    case FETCH_RESERVATION_DASHBOARD:
      return {
        ...state,
        dashboard: action.dashboard,
      }
    case TOGGLE_DASHBOARD: {
      return {
        ...state,
        isDashboardToggled: action.toggle,
      }
    }
    default:
      return state
  }
}

export const getMyBookings = () => {
  return async (dispatch) => {
    try {
      const data = await API.getMyBookings()
      dispatch({ type: GET_MY_BOOKINGS, myBookings: data })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const getAmenities = (id) => {
  return async (dispatch) => {
    try {
      const data = await API.getAmenities(id)
      dispatch({ type: GET_AMENITIES, amenities: data })
    } catch (err) {
      throw err
    }
  }
}

export const addNewAmenity = (amenityData) => {
  return async (dispatch) => {
    try {
      const data = await API.createNewAmenity(amenityData)
      dispatch({ type: ADD_NEW_AMENITY, amenity: data })
      return data
    } catch ({ message }) {
      throw new Error(message)
    }
  }
}

export const editAmenity = (amenityData, amenityId) => {
  return async (dispatch) => {
    const { data } = await API.editAmenity(amenityData, amenityId)
    dispatch(upsertAmenity(data))
    return data
  }
}

export const upsertAmenity = (amenity) => ({ type: UPSERT_AMENITY, amenity })

export const updateAmenityQuestions = (amenityId, questions) => {
  return async (dispatch) => {
    try {
      const response = await API.updateAmenityQuestions(amenityId, questions)
      dispatch(upsertAmenity(response.data))
    } catch ({ message }) {
      throw new Error(message)
    }
  }
}

export const cancelAmenityReservations = (reservations, refund = false, amenityId) => {
  return async () => {
    try {
      const data = await API.cancelAmenityReservations({ reservations, refund }, amenityId)
      return data
    } catch ({ message }) {
      throw new Error(message)
    }
  }
}

export const deleteAmenity = (amenityId) => {
  return async (dispatch) => {
    try {
      await API.deleteAmenity(amenityId)
      dispatch({ type: DELETE_AMENITY, amenityId })
    } catch ({ message }) {
      throw new Error(message)
    }
  }
}

export const getReservations = (buildingId, timestamp) => {
  return async (dispatch) => {
    try {
      const data = await API.getReservations(buildingId, timestamp)
      dispatch({ type: GET_RESERVATIONS, reservations: data, timestamp })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const getMultidayReservations = (buildingId, timestamp) => {
  return async (dispatch) => {
    try {
      const data = await API.getMultidayReservations(buildingId, timestamp)
      dispatch({ type: GET_MULTIDAY_RESERVATIONS, reservations: data, timestamp })
    } catch (err) {
      throw new Error(err)
    }
  }
}

// When the calendar flips months quickly, responses can land out of order - only the most recent
// request may write its list, so an older range never overwrites the one on screen.
let latestAmenityReservationsRequestId = 0
let latestMultiDayAmenityReservationsRequestId = 0

export const getAmenityReservationsInDateRange = (buildingId, from, to) => {
  return async (dispatch) => {
    const requestId = ++latestAmenityReservationsRequestId
    try {
      const data = await API.getAmenityReservationsInDateRange(buildingId, from, to)
      if (requestId !== latestAmenityReservationsRequestId) return
      dispatch({
        type: GET_AMENITY_RESERVATIONS,
        reservations: data,
      })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const getMultiDayAmenityReservationsInDateRange = (buildingId, from, to) => {
  return async (dispatch) => {
    const requestId = ++latestMultiDayAmenityReservationsRequestId
    try {
      const data = await API.getMultiDayAmenityReservationsInDateRange(buildingId, from, to)
      if (requestId !== latestMultiDayAmenityReservationsRequestId) return
      dispatch({
        type: GET_MULTIDAY_AMENITY_RESERVATIONS,
        reservations: data,
      })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const clearReservations = () => {
  return (dispatch) => {
    try {
      dispatch({ type: CLEAR_RESERVATIONS })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const getRequests = (buildingId) => {
  return async (dispatch) => {
    try {
      const data = await API.getRequests(buildingId)
      dispatch({ type: GET_REQUESTS, requests: data })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const setBlockedDays = (blockedDays) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_BLOCKED_DAYS, blockedDays })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const setRequestStatus = (requestData, status, refund, cancelNote) => {
  return async (dispatch) => {
    try {
      const data = {
        reservations: requestData,
        cancelNote,
      }
      await API.setRequestStatus(data, status, refund, cancelNote)
      dispatch({ type: SET_STATUS, ids: requestData })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const cancelBooking = (bookingId) => {
  return async (dispatch) => {
    try {
      const updatedData = await API.cancelBooking(bookingId)
      dispatch({ type: CANCEL_BOOKING, bookingId, updatedData })
    } catch (err) {
      const error = err.message ? err.message : err
      return error
    }
  }
}

export const cancelReservation = ({
  reservationId,
  timestamp,
  refund,
  withoutStoreUpdate,
  cancelNote,
  amount,
}) => {
  return async (dispatch) => {
    try {
      const updatedData = await API.cancelReservation(reservationId, refund, cancelNote, amount)
      !withoutStoreUpdate &&
        dispatch({ type: CANCEL_RESERVATION, reservationId, timestamp, updatedData })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const cancelAmenityReservation = (reservationId, refund) => {
  return async (dispatch) => {
    try {
      const updatedData = await API.cancelReservation(reservationId, refund)
      dispatch({ type: CANCEL_AMENITY_RESERVATION, reservationId, updatedData })
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const fetchReservationDashboard =
  ({
    buildingId = 0,
    page = 1,
    limit = 50,
    status = 'all',
    search = '',
    dateStart,
    dateEnd,
    amenityId,
    locationId,
  }) =>
  (dispatch) => {
    const skip = (page - 1) * limit

    dispatch({ type: TOGGLE_DASHBOARD, toggle: false })

    return API.getReservationDashboard({
      buildingId,
      skip,
      limit,
      status,
      search,
      dateStart,
      dateEnd,
      amenityId,
      locationId,
    })
      .then((res) => {
        dispatch({ type: FETCH_RESERVATION_DASHBOARD, dashboard: res.data })
        return Promise.resolve(res.data)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }

export const deleteTimeBlock = (timeBlockId) => {
  return async () => {
    try {
      await API.deleteTimeBlock(timeBlockId)
    } catch (err) {
      throw new Error(err)
    }
  }
}

export const toggleDashboard = () => (dispatch) =>
  dispatch({ type: TOGGLE_DASHBOARD, toggle: true })

export const selectMyBookings = (state) =>
  state.reservations.myBookings.filter((booking) => !!booking.amenityId)

export const selectAmenities = (state) => state.reservations.amenities

export const selectReservations = (state) => state.reservations.reservations

export const selectAmenityReservations = (state) => state.reservations.amenityReservations

export const selectBlockedDays = (state) => state.reservations.blockedDays

export const selectMultiDayAmenityReservations = (state) =>
  state.reservations.amenityMultiDayReservations

export const selectRequests = (state) => state.reservations.requests

export const selectAmenityById = createSelector(selectAmenities, (amenities) => (id) => {
  if (id === 'new') {
    return {}
  }

  return amenities.find((amenity) => amenity.id === parseInt(id)) || amenities[0] || {}
})

export const selectAmenitiesForFilter = createSelector(selectAmenities, (amenities) =>
  amenities.map((amenity) => ({ id: amenity.id, text: amenity.name, value: amenity.id })),
)

export const selectSpacesForFilter = createSelector(selectAmenities, (amenities) =>
  amenities.flatMap((amenity) =>
    (amenity.locations ?? []).map((location) => ({
      id: location.id,
      text: location.name,
      value: location.id,
    })),
  ),
)

export const selectAmenitiesForGiftCards = createSelector(selectAmenities, (amenities) => {
  const filtered = amenities.filter(
    (amenity) =>
      !amenity.hidden &&
      (amenity.price > 0 || amenity.extraFee > 0) &&
      (amenity.multiDayBooking || amenity.endDate > new Date().getTime()),
  )

  return filtered
})

export const selectDashboardData = (state) => state.reservations.dashboard

export const selectIsDashboardToggled = (state) => state.reservations.isDashboardToggled

export default reservationsReducer
